// Tax Policy layer — client-side mirror of the backend matcher
// (apps/operations/src/modules/taxes/tax-policies/tax-policy-match.ts), so
// the estimate form's live totals react to the selected business unit the
// same way the server will price the save. Keep the two in lockstep: same
// axis weights, same tie-break, same intersection semantics.
import type { AppliedTaxPolicy, TaxGroup, TaxPolicy, TaxResolutionResult } from '@/types/tax';

export interface TaxPolicyPreviewContext {
  businessUnitId?: string | null;
  /** RESIDENTIAL | COMMERCIAL | null — derived. */
  segment?: string | null;
  jobTypeId?: string | null;
  /** Upper-cased service-address state. */
  stateCode?: string | null;
  /** The service-address ZIP/postal, matched against a rule's coverage. */
  zipCode?: string | null;
}

const AXIS_WEIGHTS = {
  businessUnitId: 8,
  jobTypeId: 6,
  segment: 4,
  stateCode: 1,
} as const;

/** Area sits between segment (4) and state (1) — backend parity. */
const AREA_WEIGHT = 2;

type Axis = keyof typeof AXIS_WEIGHTS;
const AXES: readonly Axis[] = ['businessUnitId', 'jobTypeId', 'segment', 'stateCode'];

/**
 * Client mirror of the backend's postal-area normalization: uppercase, no
 * whitespace; a US ZIP+4 collapses to its 5-digit ZIP; a Canadian postal
 * also yields its FSA as a match candidate.
 */
export function postalCandidates(value: string): string[] {
  const cleaned = value.trim().toUpperCase().replace(/\s+/g, '');
  if (!cleaned) return [];
  const us = cleaned.match(/^(\d{5})(?:-\d{4})?$/);
  if (us) return [us[1]!];
  const out = [cleaned];
  const fsa = cleaned.match(/^([A-Z]\d[A-Z])(?:\d[A-Z]\d)?$/);
  if (fsa && fsa[1] !== cleaned) out.push(fsa[1]!);
  return out;
}

export function normalizePostalArea(value: string): string {
  return postalCandidates(value)[0] ?? value.trim().toUpperCase();
}

function norm(value: string | null | undefined): string | null {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** Mirror of the backend `segmentFromContactType` (PROPERTY_MANAGER → COMMERCIAL). */
export function segmentFromContactType(
  contactType: string | null | undefined,
): 'RESIDENTIAL' | 'COMMERCIAL' | null {
  switch ((contactType ?? '').toUpperCase()) {
    case 'RESIDENTIAL':
      return 'RESIDENTIAL';
    case 'COMMERCIAL':
    case 'PROPERTY_MANAGER':
      return 'COMMERCIAL';
    default:
      return null;
  }
}

/**
 * The unit axis as a SET — backend parity with scoreTaxPolicy.
 *
 * Empty means "not scoped by unit". The legacy scalar stands in only while a
 * rule has no set, so a rule saved before the multi-unit axis scores the same
 * here as it does on the server.
 */
function wantedUnits(policy: TaxPolicy): string[] {
  const ids = policy.businessUnitIds ?? [];
  if (ids.length > 0) {
    return ids.map((u) => norm(u)).filter((u): u is string => u !== null);
  }
  const scalar = norm(policy.businessUnitId);
  return scalar === null ? [] : [scalar];
}

function scorePolicy(policy: TaxPolicy, ctx: TaxPolicyPreviewContext): number | null {
  let score = 0;
  for (const axis of AXES) {
    if (axis === 'businessUnitId') continue; // set-valued — handled below
    const want = norm(policy[axis]);
    if (want === null) continue;
    const have = norm(ctx[axis]);
    if (have === null || want !== have) return null;
    // Server parity: a segment inherited from a both-segment rule restricts,
    // never ranks.
    if (axis === 'segment' && policy.legacyAnySegment) continue;
    score += AXIS_WEIGHTS[axis];
  }

  const units = wantedUnits(policy);
  if (units.length > 0) {
    const have = norm(ctx.businessUnitId);
    if (have === null || !units.includes(have)) return null;
    // Same score for one unit or five: specificity is "this axis is
    // constrained", not how many values it lists.
    score += AXIS_WEIGHTS.businessUnitId;
  }
  // Location axis — backend parity: empty = not scoped by area; both sides
  // go through postal normalization so ZIP+4 matches a 5-digit scope.
  const zips = policy.zips ?? [];
  // The rule's own area list is an allow-list, always. Allow/block belongs to
  // the individual override, which never affects whether the rule MATCHES.
  if (zips.length > 0) {
    const raw = norm(ctx.zipCode);
    if (raw === null) return null;
    const candidates = new Set(postalCandidates(raw));
    if (candidates.size === 0) return null;
    const hit = zips.some((z) => candidates.has(normalizePostalArea(z.zipCode)));
    if (!hit) return null;
    score += AREA_WEIGHT;
  }
  return score;
}

/**
 * EVERY rule that matches the context, most-specific first, using the same
 * scoring and tie-break as the winner. The editor shows this so an operator can
 * see who else claims the document and set a priority that actually decides it —
 * a rule losing silently is the failure this is meant to expose.
 */
export function rankTaxPolicyPreview(
  policies: readonly TaxPolicy[],
  ctx: TaxPolicyPreviewContext,
): Array<{ policy: TaxPolicy; score: number }> {
  const scored: Array<{ policy: TaxPolicy; score: number }> = [];
  for (const policy of policies) {
    if (!policy.isActive) continue;
    const score = scorePolicy(policy, ctx);
    if (score === null) continue;
    scored.push({ policy, score });
  }
  return scored.sort((a, b) =>
    b.score !== a.score
      ? b.score - a.score
      : a.policy.priority !== b.policy.priority
        ? a.policy.priority - b.policy.priority
        : a.policy.name !== b.policy.name
          ? a.policy.name < b.policy.name
            ? -1
            : 1
          : a.policy.id < b.policy.id
            ? -1
            : 1,
  );
}

/**
 * WHY a rule did not match — the first axis that excluded the document.
 *
 * "It does not match" is useless feedback while writing a rule: every axis is a
 * silent AND, so one wrong field makes the whole rule disappear with no hint
 * which. Returns null when the rule does match.
 */
export function explainPolicyMismatch(
  policy: TaxPolicy,
  ctx: TaxPolicyPreviewContext,
): { axis: string; wants: string; has: string } | null {
  const LABELS: Record<Axis, string> = {
    businessUnitId: 'Business units',
    jobTypeId: 'Job type',
    segment: 'Customer segment',
    stateCode: 'State',
  };
  const units = wantedUnits(policy);
  if (units.length > 0) {
    const have = norm(ctx.businessUnitId);
    if (have === null || !units.includes(have)) {
      return {
        axis: LABELS.businessUnitId,
        wants: `${units.length} unit${units.length === 1 ? '' : 's'}`,
        has: have === null ? 'no unit selected' : 'a unit not on the rule',
      };
    }
  }
  for (const axis of AXES) {
    if (axis === 'businessUnitId') continue;
    const want = norm(policy[axis]);
    if (want === null) continue;
    const have = norm(ctx[axis]);
    if (have === null || want !== have) {
      return {
        axis: LABELS[axis],
        wants: String(policy[axis] ?? ''),
        has: have === null ? 'nothing selected' : String(ctx[axis] ?? ''),
      };
    }
  }
  const zips = policy.zips ?? [];
  if (zips.length > 0) {
    const raw = norm(ctx.zipCode);
    if (raw === null) {
      return { axis: 'Service areas', wants: `${zips.length} ZIPs`, has: 'no location chosen' };
    }
    const candidates = new Set(postalCandidates(raw));
    const hit = zips.some((z) => candidates.has(normalizePostalArea(z.zipCode)));
    if (!hit) {
      return {
        axis: 'Service areas',
        wants: `one of ${zips.length} ZIPs`,
        has: String(ctx.zipCode ?? ''),
      };
    }
  }
  return null;
}

/** Pick the winning rule: specificity desc → priority asc → name → id. */
export function matchTaxPolicyPreview(
  policies: readonly TaxPolicy[],
  ctx: TaxPolicyPreviewContext,
): TaxPolicy | null {
  let best: TaxPolicy | null = null;
  let bestScore = -1;
  for (const policy of policies) {
    if (!policy.isActive) continue;
    const score = scorePolicy(policy, ctx);
    if (score === null) continue;
    if (
      best === null ||
      score > bestScore ||
      (score === bestScore &&
        (policy.priority < best.priority ||
          (policy.priority === best.priority &&
            (policy.name < best.name || (policy.name === best.name && policy.id < best.id)))))
    ) {
      best = policy;
      bestScore = score;
    }
  }
  return best;
}

function isNoNarrowing(taxableBases: readonly string[]): boolean {
  return taxableBases.includes('all');
}

function intersectAppliesTo(
  appliesTo: string | string[] | null | undefined,
  taxableBases: readonly string[],
): string[] {
  const set = Array.isArray(appliesTo)
    ? appliesTo
    : typeof appliesTo === 'string' && appliesTo.length > 0
      ? [appliesTo]
      : [];
  if (isNoNarrowing(taxableBases)) return set;
  if (set.includes('all')) return [...taxableBases];
  return set.filter((v) => taxableBases.includes(v));
}

/**
 * Apply the winning rule to a resolved result the way the server does:
 * a forced group swaps the base (resolvedFrom POLICY) when it can be found
 * in `allGroups` — except under a MANUAL pin, which beats the forced group
 * on the server too — then every item's applies-to is narrowed by
 * intersection (items are narrowed, never removed) and the applied policy
 * is stamped. A forced group that cannot be found locally still stamps the
 * badge so the operator knows the save will differ.
 */
export function applyTaxPolicyPreview(
  resolution: TaxResolutionResult | null,
  policies: readonly TaxPolicy[] | undefined,
  ctx: TaxPolicyPreviewContext,
  allGroups?: readonly TaxGroup[],
): TaxResolutionResult | null {
  if (!policies || policies.length === 0) return resolution;
  // Server parity: resolveTax returns the exempt result BEFORE any policy
  // matching, so an exempt customer never carries an applied rule.
  if (resolution?.exempt) return resolution;
  const winner = matchTaxPolicyPreview(policies, ctx);
  if (!winner) return resolution;
  // An address with no zone (the hook found nothing and there is no org
  // default) is the server's NONE, and the server still applies the winning
  // rule there: a forced group is swapped in, a stated rate is charged. So
  // the preview normalises "no resolution" to NONE and lets both run.
  const normalised: TaxResolutionResult = resolution ?? {
    exempt: false,
    group: null,
    resolvedFrom: 'NONE',
  };

  const applied: AppliedTaxPolicy = {
    id: winner.id,
    name: winner.name,
    taxableBases: [...winner.taxableBases],
    ...(winner.taxGroupId ? { forcedTaxGroupId: winner.taxGroupId } : {}),
  };

  // Forced-group swap — server parity: a manual pin wins over the forced
  // group; otherwise the forced group replaces the geographic one.
  let base = normalised;
  if (winner.taxGroupId && normalised.resolvedFrom !== 'MANUAL') {
    const forced = (allGroups ?? []).find(
      (g) => g.id === winner.taxGroupId && g.isActive !== false && !g.placeholderReason,
    );
    if (forced) {
      base = { ...normalised, group: forced, resolvedFrom: 'POLICY' };
    }
  }

  // Server parity: where geography (and any forced group) has no rates — no
  // group, or a provider-no-data placeholder with nothing in it — a rule that
  // states a rate is charged on its own, and it is the source (POLICY).
  if (ruleSuppliesRates(winner, ctx.zipCode) && (!base.group || base.group.items.length === 0)) {
    base = { ...base, group: base.group ?? ruleOnlyGroup(winner), resolvedFrom: 'POLICY' };
  }

  if (!base.group) return { ...base, policy: applied };

  const narrowedItems = base.group.items.map((item) => ({
    ...item,
    taxCode: {
      ...item.taxCode,
      appliesTo: intersectAppliesTo(item.taxCode.appliesTo, winner.taxableBases),
    },
  }));
  // Server parity: a narrowed group states the rate it still charges, so a
  // "no sales tax" rule does not leave the jurisdiction's rate in the snapshot.
  const narrowed: TaxGroup = isNoNarrowing(winner.taxableBases)
    ? base.group
    : { ...base.group, items: narrowedItems, combinedRate: combinedRateOfItems(narrowedItems) };

  return { ...base, policy: applied, group: applyBaseRatePreview(narrowed, winner, ctx.zipCode) };
}

const OVERRIDABLE_BASES = ['labor', 'materials', 'freight'];

/** Does the rule state at least one rate that is in force at this ZIP? */
function ruleSuppliesRates(policy: TaxPolicy, zipCode: string | null | undefined): boolean {
  return (policy.baseRates ?? []).some((r) => overrideCoversZip(r, zipCode));
}

/**
 * Server parity (`ruleOnlyGroup`): the empty group a rule's rows are laid on
 * where the address has none. Keyed and named after the rule, as the save
 * will stamp it.
 */
function ruleOnlyGroup(policy: TaxPolicy): TaxGroup {
  return {
    id: policy.id,
    orgId: '',
    name: policy.name,
    combinedRate: 0,
    isDefault: false,
    isActive: true,
    source: 'policy',
    items: [],
  };
}

function subtractBases(appliesTo: string | string[], remove: ReadonlySet<string>): string[] {
  const current = Array.isArray(appliesTo) ? appliesTo : appliesTo ? [appliesTo] : [];
  const expanded = current.includes('all') ? OVERRIDABLE_BASES : current;
  return expanded.filter((b) => !remove.has(b));
}

function overrideCoversZip(
  override: { zipMode?: string; zips?: string[] },
  zipCode: string | null | undefined,
): boolean {
  const list = override.zips ?? [];
  const mode = (override.zipMode ?? 'ALL').toUpperCase();
  if (mode === 'ALL' || list.length === 0) return true;
  if (!zipCode) return mode === 'EXCLUDE';
  const candidates = new Set(postalCandidates(zipCode));
  const hit = list.some((z) => candidates.has(normalizePostalArea(String(z))));
  return mode === 'EXCLUDE' ? !hit : hit;
}

/**
 * Client mirror of the server's `applyBaseRateOverrides`
 * (operations: tax-policies/tax-policy-match.ts). A rate override REPLACES: every
 * code at the layer it names stops applying to that base, and one synthetic row
 * carries the stated rate.
 *
 * Without this the estimate form's live totals showed the jurisdiction's rates
 * while the save charged the rule's — a "service at 0%" rule previewed full labor
 * tax and then saved none. Keep the two in lockstep.
 */
function applyBaseRatePreview(
  group: TaxGroup,
  policy: TaxPolicy,
  zipCode: string | null | undefined,
): TaxGroup {
  const rates = (policy.baseRates ?? []).filter((r) => overrideCoversZip(r, zipCode));
  if (rates.length === 0) return group;
  const takes = (layer: string | undefined, type: string | null) =>
    !layer || layer === 'all' || type === layer;

  const items = group.items.map((item) => {
    const taken = new Set(
      rates
        .filter((r) => takes(r.jurisdictionType, item.taxCode.jurisdictionType))
        .map((r) => r.base),
    );
    return taken.size === 0
      ? item
      : {
          ...item,
          taxCode: { ...item.taxCode, appliesTo: subtractBases(item.taxCode.appliesTo, taken) },
        };
  });

  const maxSortOrder = items.reduce((m, i) => Math.max(m, i.sortOrder), 0);
  rates.forEach((r, idx) => {
    const layer = r.jurisdictionType ?? 'all';
    const replaced = group.items.find(
      (i) =>
        takes(layer, i.taxCode.jurisdictionType) &&
        subtractBases(i.taxCode.appliesTo, new Set([r.base])).length !==
          subtractBases(i.taxCode.appliesTo, new Set()).length,
    );
    const named = r.label?.trim();
    // Server parity: a base the jurisdiction does not tax replaces nothing, but
    // the row still takes the name of the jurisdiction's tax at that layer.
    const nameSource =
      replaced ??
      [...group.items]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .find((i) => takes(layer, i.taxCode.jurisdictionType));
    const id = `policy-rate:${policy.id}:${r.base}:${layer}`;
    // Server parity: a layer-specific override takes the place (order and
    // compounding) of the code it stands in for.
    const standsIn = layer !== 'all' && replaced ? replaced : null;
    items.push({
      id,
      taxCodeId: id,
      sortOrder: standsIn ? standsIn.sortOrder : maxSortOrder + 1 + idx,
      isCompound: standsIn ? standsIn.isCompound : false,
      taxCode: {
        id,
        name:
          named ||
          nameSource?.taxCode.name ||
          (layer === 'all'
            ? `${policy.name} — ${r.base}`
            : `${policy.name} — ${r.base} (${layer})`),
        rate: r.rate,
        rateOverride: null,
        effectiveRate: r.rate,
        isOverridden: false,
        rateType: 'percentage',
        appliesTo: [r.base],
        jurisdiction: named || nameSource?.taxCode.jurisdiction || policy.name,
        jurisdictionType: layer === 'all' ? null : layer,
        ...(r.hideOnDocument ? { hideOnDocument: true } : {}),
      },
    });
  });
  // Shown in the form's financials and frozen into its local snapshots, so it
  // has to be the rate the overridden group charges — server parity with
  // `combinedRateOfItems`: the most any single base pays.
  return { ...group, items, combinedRate: combinedRateOfItems(items) };
}

function combinedRateOfItems(items: TaxGroup['items']): number {
  let max = 0;
  for (const base of OVERRIDABLE_BASES) {
    let sum = 0;
    for (const item of items) {
      if ((item.taxCode.rateType ?? 'percentage') !== 'percentage') continue;
      const set = Array.isArray(item.taxCode.appliesTo)
        ? item.taxCode.appliesTo
        : item.taxCode.appliesTo
          ? [item.taxCode.appliesTo]
          : [];
      if (set.includes('all') || set.includes(base)) sum += item.taxCode.rate;
    }
    max = Math.max(max, sum);
  }
  return Number(max.toFixed(6));
}

/** Minimal unit shape the segment decision needs. */
interface SegmentSourceUnit {
  id: string;
  segment: 'RESIDENTIAL' | 'COMMERCIAL' | string;
}

/**
 * Decide the preview's segment — mirroring the resolver's precedence, and
 * the two failure modes reviews caught on the way here:
 *
 * 1. The unit decides (RESIDENTIAL/COMMERCIAL); with no unit the contact
 *    does, and an unknown customer is RESIDENTIAL — every rule names one.
 * 2. "The unit's row hasn't loaded yet" is NOT "the unit defers": while a
 *    selected unit's segment is unknown, the preview must not fall back to
 *    the contact (that would flash the old contact-first precedence), so
 *    `known: false` tells the caller to skip policy narrowing entirely.
 * 3. The unit may be ARCHIVED — absent from the active list forever — so
 *    the decision accepts a separately hydrated unit as a fallback source;
 *    an estimate saved against an archived commercial unit must preview
 *    under the commercial rule exactly as it will save.
 */
export function resolvePreviewSegment(args: {
  /** The unit selected on the document, or null. */
  buForTax: string | null;
  /** The unit's row from the active list, if present there. */
  unitFromList: SegmentSourceUnit | null | undefined;
  /** The unit's row hydrated individually (any status), if loaded. */
  hydratedUnit: SegmentSourceUnit | null | undefined;
  /** segmentFromContactType(contact.contactType). */
  contactSegment: string | null;
}): { segment: string | null; known: boolean } {
  const { buForTax, unitFromList, hydratedUnit, contactSegment } = args;
  if (!buForTax) return { segment: contactSegment ?? 'RESIDENTIAL', known: true };
  // The hydrated row wins when both are present: it is keyed to exactly
  // this unit and refetched per-selection, while the list is a shared
  // cache that can lag a BU segment edit — the "live inputs" contract the
  // resolver pins must hold in the preview too.
  const unit =
    (hydratedUnit && hydratedUnit.id === buForTax ? hydratedUnit : null) ??
    (unitFromList && unitFromList.id === buForTax ? unitFromList : null);
  if (!unit) return { segment: null, known: false };
  return { segment: unit.segment, known: true };
}

/**
 * Which axes make up a matching rule's specificity, with their weights — the
 * sum is the score `rankTaxPolicyPreview` ranks by. Lets the editor say WHY one
 * rule beats another ("HVAC unit 8 + residential 4 + ZIP area 2") instead of a
 * bare number. Empty for a rule that names no axis (or does not match).
 */
export function policyScoreParts(
  policy: TaxPolicy,
  ctx: TaxPolicyPreviewContext,
): Array<{ label: string; weight: number }> {
  if (scorePolicy(policy, ctx) === null) return [];
  const parts: Array<{ label: string; weight: number }> = [];
  if (wantedUnits(policy).length > 0) {
    parts.push({ label: 'business unit', weight: AXIS_WEIGHTS.businessUnitId });
  }
  if (norm(policy.jobTypeId) !== null) {
    parts.push({ label: 'job type', weight: AXIS_WEIGHTS.jobTypeId });
  }
  if (norm(policy.segment) !== null && !policy.legacyAnySegment) {
    parts.push({ label: String(policy.segment).toLowerCase(), weight: AXIS_WEIGHTS.segment });
  }
  if ((policy.zips ?? []).length > 0) {
    parts.push({ label: 'ZIP area', weight: AREA_WEIGHT });
  }
  if (norm(policy.stateCode) !== null) {
    parts.push({ label: `state ${policy.stateCode}`, weight: AXIS_WEIGHTS.stateCode });
  }
  return parts;
}
